/**
 * IELTS V2 Speaking — Hub + Session (System Check → Parts → Submit) + Results.
 * Routes:
 *   /resources/ielts/speaking                 → hub / mode picker
 *   /resources/ielts/speaking/session/:aid    → live session shell
 *   /resources/ielts/speaking/results/:aid    → feedback centre
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Mic, MicOff, Play, Square, Clock, ChevronRight, ArrowLeft,
  CheckCircle2, AlertTriangle, RotateCcw, Volume2, Sparkles, Award, Compass,
  Headphones, Timer,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ax = axios.create({ baseURL: BACKEND, withCredentials: true });
const DISCLAIMER = 'This AI-assisted assessment is provided by Winhouse Tutors for practice and preparation. It is not an official IELTS result and may differ from an assessment by a qualified human examiner.';

const CRIT_LABEL = {
  fluency_coherence: 'Fluency & Coherence',
  lexical_resource: 'Lexical Resource',
  grammatical_range_accuracy: 'Grammatical Range & Accuracy',
  pronunciation: 'Pronunciation',
};
const critLabel = (k) => CRIT_LABEL[k] || (k || '').replace(/_/g, ' ');

/* ================================================================== */
export function IeltsSpeakingHubPage() {
  const [menu, setMenu] = useState(false);
  const nav = useNavigate();
  const [starting, setStarting] = useState(null);
  const startMode = async (mode) => {
    setStarting(mode);
    try {
      const r = await ax.post('/api/ielts/v2/speaking/attempts/start', { mode });
      nav(`/resources/ielts/speaking/session/${r.data.attempt.id}`);
    } catch (e) {
      if (e?.response?.status === 401) { nav('/login?redirect=/resources/ielts/speaking'); return; }
      toast.error(e?.response?.data?.detail || 'Could not start');
    } finally { setStarting(null); }
  };
  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation mobileMenuOpen={menu} setMobileMenuOpen={setMenu} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-16">
        <button onClick={() => nav('/resources/ielts')}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to IELTS Hub
        </button>
        <div className="mt-3 rounded-2xl bg-gradient-to-br from-orange-500 via-rose-500 to-pink-500 text-white p-5 md:p-6"
          data-testid="ielts-speaking-hero">
          <div className="text-[11px] uppercase tracking-widest text-orange-100 font-bold">IELTS Speaking · Practice with AI Feedback</div>
          <h1 className="text-2xl md:text-3xl font-black mt-1">Winhouse IELTS Speaking Practice</h1>
          <p className="text-sm text-orange-50 mt-1 max-w-2xl">
            Record realistic IELTS Speaking practice — Part 1, cue-card Part 2,
            and Part 3 discussion. Receive an instant AI-assisted estimated
            practice band with sentence-level coaching. Pronunciation is
            marked as text-inferred; audio-based analysis will follow.
          </p>
          <div className="mt-2 text-[11px] text-orange-50 italic">{DISCLAIMER}</div>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="ielts-speaking-modes">
          {[
            ['full',  'Full Practice',   'Part 1 → Part 2 → Part 3', 'from-orange-500 to-rose-500'],
            ['part1', 'Part 1 only',     'Familiar-topic questions', 'from-blue-500 to-indigo-500'],
            ['part2', 'Part 2 only',     'Cue-card long turn (with prep)', 'from-emerald-500 to-teal-500'],
            ['part3', 'Part 3 only',     'Extended discussion', 'from-purple-500 to-pink-500'],
          ].map(([k, title, desc, g]) => (
            <button key={k} onClick={() => startMode(k)} disabled={!!starting}
              data-testid={`ielts-speaking-mode-${k}`}
              className={`text-left rounded-2xl bg-gradient-to-br ${g} text-white p-4 hover:scale-[1.01] transition disabled:opacity-60`}>
              <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Practice mode</div>
              <div className="text-lg font-black mt-1">{title}</div>
              <div className="text-xs opacity-90 mt-1">{desc}</div>
              <div className="mt-3 text-xs font-bold inline-flex items-center gap-1">
                {starting === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Start session
              </div>
            </button>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}

/* ================================================================== */
function useRecorder() {
  const [state, setState] = useState('idle'); // idle | recording | stopped | error
  const [error, setError] = useState(null);
  const [blob, setBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAt = useRef(0);
  const streamRef = useRef(null);

  const start = useCallback(async () => {
    setError(null);
    setBlob(null);
    chunksRef.current = [];
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error('unsupported');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: 'audio/webm' });
        setBlob(b);
        setDuration(Date.now() - startedAt.current);
        stream.getTracks().forEach((t) => t.stop());
        setState('stopped');
      };
      startedAt.current = Date.now();
      rec.start();
      recRef.current = rec;
      setState('recording');
    } catch (e) {
      setState('error');
      setError(e.name === 'NotAllowedError' ? 'permission_denied'
             : e.message === 'unsupported' ? 'unsupported'
             : 'unknown');
    }
  }, []);
  const stop = useCallback(() => {
    if (recRef.current && recRef.current.state === 'recording') recRef.current.stop();
  }, []);
  const reset = useCallback(() => {
    setState('idle'); setBlob(null); setDuration(0); setError(null);
  }, []);
  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); }, []);
  return { state, error, blob, duration, start, stop, reset };
}

async function blobToB64(blob) {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

/* ================================================================== */
export function IeltsSpeakingSessionPage() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [phase, setPhase] = useState('mic_check'); // mic_check | part1 | part2_prep | part2_speak | part3 | submitting
  const [p1Idx, setP1Idx] = useState({ group: 0, q: 0 });
  const [p3Idx, setP3Idx] = useState({ group: 0, q: 0 });
  const [prepRemaining, setPrepRemaining] = useState(60);
  const [saved, setSaved] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await ax.get(`/api/ielts/v2/speaking/attempts/${aid}`);
      setAttempt(r.data.attempt);
      setSaved(r.data.attempt.recordings_meta || {});
    } catch (e) { toast.error('Unable to load session'); }
  }, [aid]);
  useEffect(() => { load(); }, [load]);

  // Prep timer
  useEffect(() => {
    if (phase !== 'part2_prep') return;
    setPrepRemaining(60);
    const t = setInterval(() => setPrepRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [phase]);
  useEffect(() => {
    if (phase === 'part2_prep' && prepRemaining === 0) setPhase('part2_speak');
  }, [phase, prepRemaining]);

  const saveRecording = async (ref, blob, duration, transcript = '') => {
    const b64 = await blobToB64(blob);
    await ax.post(`/api/ielts/v2/speaking/attempts/${aid}/record`, {
      question_ref: ref, audio_b64: b64, mime: 'audio/webm',
      duration_ms: duration, transcript,
    });
    setSaved((m) => ({ ...m, [ref]: { size_bytes: blob.size, duration_ms: duration, transcript } }));
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await ax.post(`/api/ielts/v2/speaking/attempts/${aid}/submit`, {}, { timeout: 120000 });
      let mockCtx = null;
      try { mockCtx = JSON.parse(localStorage.getItem('ielts_mock_context') || 'null'); } catch { mockCtx = null; }
      if (mockCtx?.mockId && mockCtx.section === 'speaking') {
        nav(`/resources/ielts/mock/session/${mockCtx.mockId}`);
      } else {
        nav(`/resources/ielts/speaking/results/${aid}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
      setSubmitting(false);
    }
  };

  if (!attempt) return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-100"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const p1Groups = attempt.part1_items || [];
  const p3Groups = attempt.part3_items || [];
  const p1Group = p1Groups[p1Idx.group];
  const p3Group = p3Groups[p3Idx.group];
  const p2 = attempt.part2_item;
  const hasP1 = p1Groups.length > 0;
  const hasP2 = !!p2;
  const hasP3 = p3Groups.length > 0;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 text-slate-100 overflow-y-auto"
      data-testid="ielts-speaking-shell">
      <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur border-b border-slate-700/40 px-4 md:px-8 py-3 flex items-center gap-3 flex-wrap">
        <div className="text-[10px] md:text-xs font-black tracking-widest text-orange-300">WINHOUSE · IELTS SPEAKING</div>
        <span className="text-xs text-slate-400 hidden md:inline">
          Mode: <b className="text-slate-100">{(attempt.mode || 'full').toUpperCase()}</b>
        </span>
        <div className="ml-auto flex items-center gap-3">
          <button onClick={() => nav('/resources/ielts/speaking')}
            className="text-xs text-slate-400 hover:text-slate-100">Exit</button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 md:px-8 py-6">
        {phase === 'mic_check' && <MicCheck onContinue={() => setPhase(hasP1 ? 'part1' : hasP2 ? 'part2_prep' : 'part3')} />}
        {phase === 'part1' && hasP1 && (
          <Part1Runner group={p1Group} idx={p1Idx}
            onDone={() => {
              if (p1Idx.q + 1 < p1Group.questions.length) setP1Idx({ ...p1Idx, q: p1Idx.q + 1 });
              else if (p1Idx.group + 1 < p1Groups.length) setP1Idx({ group: p1Idx.group + 1, q: 0 });
              else setPhase(hasP2 ? 'part2_prep' : hasP3 ? 'part3' : 'submitting');
            }}
            onSave={(blob, dur, transcript) => saveRecording(`p1_${p1Group.id}_q${p1Idx.q}`, blob, dur, transcript)}
            saved={saved[`p1_${p1Group.id}_q${p1Idx.q}`]} />
        )}
        {phase === 'part2_prep' && hasP2 && <Part2Prep p2={p2} remaining={prepRemaining} onSkip={() => setPhase('part2_speak')} />}
        {phase === 'part2_speak' && hasP2 && (
          <Part2Speak p2={p2}
            onDone={() => setPhase(hasP3 ? 'part3' : 'submitting')}
            onSave={(blob, dur, transcript) => saveRecording(`p2_${p2.id}`, blob, dur, transcript)}
            saved={saved[`p2_${p2.id}`]} />
        )}
        {phase === 'part3' && hasP3 && (
          <Part3Runner group={p3Group} idx={p3Idx}
            onDone={() => {
              if (p3Idx.q + 1 < p3Group.questions.length) setP3Idx({ ...p3Idx, q: p3Idx.q + 1 });
              else if (p3Idx.group + 1 < p3Groups.length) setP3Idx({ group: p3Idx.group + 1, q: 0 });
              else setPhase('submitting');
            }}
            onSave={(blob, dur, transcript) => saveRecording(`p3_${p3Group.id}_q${p3Idx.q}`, blob, dur, transcript)}
            saved={saved[`p3_${p3Group.id}_q${p3Idx.q}`]} />
        )}
        {phase === 'submitting' && (
          <div className="text-center py-10" data-testid="ielts-speaking-submit-phase">
            <h2 className="text-2xl font-black">You've completed the session</h2>
            <p className="text-sm text-slate-400 mt-2">Submit to receive your AI-assisted assessment.</p>
            <button onClick={submit} disabled={submitting} data-testid="ielts-speaking-submit"
              className="mt-6 px-6 py-3 rounded-full bg-orange-500 hover:bg-orange-400 text-white font-black inline-flex items-center gap-2 disabled:opacity-60">
              {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Grading — up to 90 seconds…</> : <>Submit for AI Feedback <ChevronRight className="h-4 w-4" /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MicCheck({ onContinue }) {
  const r = useRecorder();
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (r.blob) setPreviewUrl(URL.createObjectURL(r.blob));
  }, [r.blob]);
  const errMsg = {
    permission_denied: 'Microphone permission was denied. Open your browser settings for this site and allow microphone access, then reload.',
    unsupported: 'Your browser does not support recording. Please try the latest version of Chrome, Firefox or Safari.',
    unknown: 'We could not access your microphone. Please check any other apps using the mic, then try again.',
  }[r.error];
  return (
    <div data-testid="ielts-speaking-mic-check">
      <div className="text-[11px] uppercase tracking-widest text-orange-300 font-bold">Microphone check</div>
      <h2 className="text-2xl md:text-3xl font-black mt-1">Let's make sure your microphone works</h2>
      <p className="text-sm text-slate-300 mt-2">Record a short sample below and play it back. When you can hear yourself clearly, continue.</p>
      <div className="mt-4 rounded-xl bg-slate-900/60 border border-slate-700/40 p-5">
        {r.state === 'error' ? (
          <div className="text-sm text-rose-300 flex gap-2"><AlertTriangle className="h-4 w-4 mt-0.5" /> {errMsg}</div>
        ) : null}
        <div className="flex items-center gap-3 flex-wrap">
          {r.state !== 'recording' && (
            <button onClick={r.start} data-testid="mic-check-record"
              className="px-5 py-2 rounded-full bg-orange-500 hover:bg-orange-400 text-white font-bold inline-flex items-center gap-2">
              <Mic className="h-4 w-4" /> {r.blob ? 'Record again' : 'Start recording'}
            </button>
          )}
          {r.state === 'recording' && (
            <button onClick={r.stop} data-testid="mic-check-stop"
              className="px-5 py-2 rounded-full bg-rose-500 hover:bg-rose-400 text-white font-bold inline-flex items-center gap-2">
              <Square className="h-4 w-4" /> Stop
            </button>
          )}
          {previewUrl && <audio controls src={previewUrl} data-testid="mic-check-playback" className="h-8" />}
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button onClick={onContinue} data-testid="mic-check-continue"
          disabled={r.state === 'recording'}
          className="px-5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-black inline-flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" /> Continue to speaking practice
        </button>
        <div className="text-[11px] text-slate-500">You can also continue without a sample if you're happy.</div>
      </div>
    </div>
  );
}

function Recorder({ label, testid, onSave, saved, maxSeconds }) {
  const r = useRecorder();
  const [transcript, setTranscript] = useState('');
  const [timeLeft, setTimeLeft] = useState(maxSeconds || null);
  useEffect(() => {
    if (r.state !== 'recording' || !maxSeconds) return;
    setTimeLeft(maxSeconds);
    const t = setInterval(() => setTimeLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [r.state, maxSeconds]);
  useEffect(() => { if (timeLeft === 0 && r.state === 'recording') r.stop(); }, [timeLeft, r]);
  const preview = useMemo(() => (r.blob ? URL.createObjectURL(r.blob) : null), [r.blob]);
  const doSave = async () => {
    if (!r.blob) return;
    try { await onSave(r.blob, r.duration, transcript); toast.success('Saved'); }
    catch (e) { toast.error('Save failed'); }
  };
  return (
    <div className="rounded-xl bg-slate-900/60 border border-slate-700/40 p-4" data-testid={testid}>
      {label && <div className="text-[11px] uppercase tracking-widest text-orange-300 font-bold mb-2">{label}</div>}
      <div className="flex flex-wrap items-center gap-3">
        {r.state !== 'recording' && (
          <button onClick={r.start} data-testid={`${testid}-record`}
            className="px-4 py-2 rounded-full bg-orange-500 hover:bg-orange-400 text-white text-sm font-bold inline-flex items-center gap-1.5">
            <Mic className="h-4 w-4" /> {r.blob ? 'Record again' : 'Record'}
          </button>
        )}
        {r.state === 'recording' && (
          <>
            <button onClick={r.stop} data-testid={`${testid}-stop`}
              className="px-4 py-2 rounded-full bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold inline-flex items-center gap-1.5">
              <Square className="h-4 w-4" /> Stop
            </button>
            {maxSeconds && timeLeft != null && (
              <span className="text-xs text-slate-400"><Timer className="h-3.5 w-3.5 inline mr-1" />{Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}</span>
            )}
            <span className="text-xs text-rose-400 animate-pulse">● recording</span>
          </>
        )}
        {preview && <audio controls src={preview} className="h-8" data-testid={`${testid}-playback`} />}
        {r.blob && (
          <button onClick={doSave} data-testid={`${testid}-save`}
            className="ml-auto px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-black inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" /> Save & continue
          </button>
        )}
      </div>
      {r.state === 'error' && (
        <div className="mt-2 text-xs text-rose-300">Microphone access failed. Check permissions and try again.</div>
      )}
      <div className="mt-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Optional: quick text notes (helps AI feedback while audio transcription is being added)</div>
        <textarea value={transcript} onChange={(e) => setTranscript(e.target.value)}
          data-testid={`${testid}-transcript`}
          className="mt-1 w-full min-h-[80px] bg-slate-950/50 border border-slate-700 focus:border-orange-400 rounded p-2 text-xs text-slate-100 outline-none"
          placeholder="A rough transcript of what you said (or leave blank if you prefer)…" />
      </div>
      {saved && <div className="mt-2 text-[11px] text-emerald-400">✓ Saved · {Math.round((saved.duration_ms || 0) / 1000)}s</div>}
    </div>
  );
}

function Part1Runner({ group, idx, onDone, onSave, saved }) {
  const q = group.questions[idx.q];
  return (
    <div data-testid={`speaking-part1-${group.id}-q${idx.q}`}>
      <div className="text-[11px] uppercase tracking-widest text-orange-300 font-bold">
        Part 1 · {group.topic} · Question {idx.q + 1} of {group.questions.length}
      </div>
      <h2 className="text-xl md:text-2xl font-black mt-1">{q}</h2>
      <div className="mt-4">
        <Recorder label="Answer briefly (about 30-45 seconds)"
          testid={`speaking-recorder-p1-q${idx.q}`}
          onSave={async (b, d, t) => { await onSave(b, d, t); onDone(); }}
          saved={saved} maxSeconds={60} />
      </div>
      <div className="mt-2 flex justify-end">
        <button onClick={onDone} className="text-xs text-slate-400 hover:text-slate-100"
          data-testid="speaking-part1-skip">Skip →</button>
      </div>
    </div>
  );
}

function Part2Prep({ p2, remaining, onSkip }) {
  return (
    <div data-testid="speaking-part2-prep">
      <div className="text-[11px] uppercase tracking-widest text-orange-300 font-bold">Part 2 · Preparation time</div>
      <h2 className="text-xl md:text-2xl font-black mt-1">{p2.title}</h2>
      <div className="mt-2 text-sm text-slate-200">You should say:</div>
      <ul className="mt-1 list-disc list-inside text-sm text-slate-200 space-y-1">
        {(p2.bullets || []).map((b, i) => <li key={i}>{b}</li>)}
      </ul>
      <div className="mt-2 text-sm text-slate-200">{p2.closing}</div>
      <div className="mt-6 rounded-xl bg-slate-900/60 border border-slate-700/40 p-4 flex items-center gap-3">
        <Clock className="h-5 w-5 text-orange-300" />
        <div className="text-sm">Preparation time — <b className="text-orange-300 tabular-nums">{Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')}</b> remaining</div>
        <button onClick={onSkip} data-testid="speaking-part2-prep-skip"
          className="ml-auto px-4 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 text-xs font-bold">I'm ready — speak now</button>
      </div>
      <div className="mt-2 text-xs text-slate-500">Use this minute to make notes mentally. When ready, click "Speak now".</div>
    </div>
  );
}

function Part2Speak({ p2, onDone, onSave, saved }) {
  return (
    <div data-testid="speaking-part2-speak">
      <div className="text-[11px] uppercase tracking-widest text-orange-300 font-bold">Part 2 · Speak now</div>
      <h2 className="text-xl md:text-2xl font-black mt-1">{p2.title}</h2>
      <div className="mt-2 text-xs text-slate-400">Aim for around 1½ to 2 minutes.</div>
      <div className="mt-4">
        <Recorder label="Long-turn response (up to 2 minutes)"
          testid="speaking-recorder-p2"
          onSave={async (b, d, t) => { await onSave(b, d, t); onDone(); }}
          saved={saved} maxSeconds={120} />
      </div>
    </div>
  );
}

function Part3Runner({ group, idx, onDone, onSave, saved }) {
  const q = group.questions[idx.q];
  return (
    <div data-testid={`speaking-part3-${group.id}-q${idx.q}`}>
      <div className="text-[11px] uppercase tracking-widest text-orange-300 font-bold">
        Part 3 · {group.theme} · Question {idx.q + 1} of {group.questions.length}
      </div>
      <h2 className="text-xl md:text-2xl font-black mt-1">{q}</h2>
      <div className="mt-4">
        <Recorder label="Extended response (60-90 seconds)"
          testid={`speaking-recorder-p3-q${idx.q}`}
          onSave={async (b, d, t) => { await onSave(b, d, t); onDone(); }}
          saved={saved} maxSeconds={120} />
      </div>
      <div className="mt-2 flex justify-end">
        <button onClick={onDone} className="text-xs text-slate-400 hover:text-slate-100"
          data-testid="speaking-part3-skip">Skip →</button>
      </div>
    </div>
  );
}

/* ================================================================== */
export function IeltsSpeakingResultsPage() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [menu, setMenu] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regrading, setRegrading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/api/ielts/v2/speaking/attempts/${aid}`);
      setData(r.data.attempt);
    } finally { setLoading(false); }
  }, [aid]);
  useEffect(() => { load(); }, [load]);

  const retry = async () => {
    setRegrading(true);
    try {
      await ax.post(`/api/ielts/v2/speaking/attempts/${aid}/assess`, {}, { timeout: 120000 });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Retry failed'); }
    finally { setRegrading(false); }
  };

  const practiseAgain = async () => {
    try {
      const r = await ax.post(`/api/ielts/v2/speaking/attempts/${aid}/improve`, { keep_questions: false });
      nav(`/resources/ielts/speaking/session/${r.data.attempt.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="min-h-screen bg-slate-50"><Navigation mobileMenuOpen={menu} setMobileMenuOpen={setMenu} /><div className="pt-32 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading…</div><Footer /></div>;
  if (!data) return null;

  const env = data.ai_assessment || {};
  const status = data.ai_assessment_status;
  const cs = env.criterion_scores || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation mobileMenuOpen={menu} setMobileMenuOpen={setMenu} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-28 md:pt-32 pb-16">
        <button onClick={() => nav('/resources/ielts/speaking')}
          className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid="ielts-speaking-results-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Speaking
        </button>
        {status !== 'completed' && (
          <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-6" data-testid="ielts-speaking-results-failed">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-amber-900">Your Speaking attempt has been saved. Automated feedback is temporarily unavailable.</div>
                <button onClick={retry} disabled={regrading}
                  data-testid="ielts-speaking-results-retry"
                  className="mt-3 px-4 py-2 rounded-full bg-amber-600 hover:bg-amber-500 text-white text-sm font-black inline-flex items-center gap-2">
                  {regrading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Retry Assessment
                </button>
              </div>
            </div>
          </div>
        )}
        {status === 'completed' && (
          <>
            <div className="mt-4 rounded-2xl bg-gradient-to-br from-orange-500 via-rose-500 to-pink-500 p-6 md:p-8 text-white shadow-xl"
              data-testid="ielts-speaking-results-headline">
              <div className="text-[11px] uppercase tracking-widest text-orange-100 font-bold">Winhouse Estimated IELTS Speaking Practice Band</div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div>
                  <div className="text-6xl md:text-7xl font-black tabular-nums" data-testid="ielts-speaking-results-band">
                    {(env.estimated_band ?? 0).toFixed(1)}
                  </div>
                  <div className="text-[11px] text-orange-50 mt-1">
                    Confidence: <b>{env.assessment_confidence}</b>
                    {env.pronunciation && !env.pronunciation.assessed_from_audio && (
                      <span className="ml-2 rounded-full px-2 py-0.5 bg-amber-950/40 border border-amber-500/40 text-amber-200 text-[10px]">
                        Pronunciation NOT assessed from audio
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-orange-100 uppercase tracking-widest font-bold mb-1">Criteria</div>
                  <div className="space-y-1.5" data-testid="ielts-speaking-results-criteria">
                    {Object.entries(cs).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-sm">
                        <div className="flex-1 text-orange-50">{critLabel(k)}</div>
                        <div className="tabular-nums font-bold w-10 text-right">{Number(v).toFixed(1)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {env.pronunciation?.note && (
                <div className="mt-3 rounded bg-orange-900/30 border border-orange-500/40 p-2 text-[11px]">
                  <b>Pronunciation (text-inferred):</b> {env.pronunciation.note}
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button onClick={practiseAgain}
                  data-testid="ielts-speaking-results-again"
                  className="px-5 py-2 rounded-full bg-white text-orange-700 hover:bg-orange-50 text-sm font-black inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> Practise Again
                </button>
                <button onClick={retry} disabled={regrading}
                  data-testid="ielts-speaking-results-retry"
                  className="px-4 py-2 rounded-full bg-orange-800/40 hover:bg-orange-800/60 text-white text-sm font-bold inline-flex items-center gap-2">
                  {regrading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Retry Assessment
                </button>
              </div>
              <div className="mt-3 text-[11px] text-orange-50 italic">{DISCLAIMER}</div>
            </div>

            {/* Summary + strengths */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="ielts-speaking-strengths">
                <div className="text-[10px] uppercase tracking-widest text-emerald-900 font-bold">What you did well</div>
                <ul className="mt-2 text-sm text-slate-800 space-y-1">{(env.strengths || []).map((s, i) => <li key={i}>✓ {s}</li>)}</ul>
              </div>
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-4" data-testid="ielts-speaking-improvements">
                <div className="text-[10px] uppercase tracking-widest text-rose-900 font-bold">Areas to improve</div>
                <ul className="mt-2 text-sm text-slate-800 space-y-1">{(env.improvement_areas || []).map((s, i) => <li key={i}>→ {s}</li>)}</ul>
              </div>
            </div>

            {env.sentence_feedback?.length > 0 && (
              <div className="mt-4 rounded-xl bg-white border border-slate-200 p-4 md:p-6" data-testid="ielts-speaking-sentence-feedback">
                <div className="text-sm font-bold text-[#172033] flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-orange-500" /> Speaking coach
                </div>
                <div className="mt-3 space-y-3">
                  {env.sentence_feedback.map((s, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                      <div className="text-[11px] uppercase tracking-widest text-rose-600 font-bold">You said</div>
                      <div className="text-sm italic text-slate-800 mt-1">"{s.your_sentence}"</div>
                      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mt-2">Issue</div>
                      <div className="text-sm text-slate-700">{s.issue}</div>
                      <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold mt-2">Try expanding</div>
                      <div className="text-sm text-emerald-800 font-medium">"{s.suggested_expansion}"</div>
                      <div className="text-[11px] uppercase tracking-widest text-indigo-600 font-bold mt-2">Why this is stronger</div>
                      <div className="text-sm text-slate-700">{s.why}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(env.part_notes || env.fluency_patterns?.length > 0) && (
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {env.part_notes && (
                  <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="ielts-speaking-part-notes">
                    <div className="text-sm font-bold text-[#172033]">Per-part notes</div>
                    <div className="mt-2 text-xs text-slate-700 space-y-1">
                      {env.part_notes.part_1 && <div><b>Part 1:</b> {env.part_notes.part_1}</div>}
                      {env.part_notes.part_2 && <div><b>Part 2:</b> {env.part_notes.part_2}</div>}
                      {env.part_notes.part_3 && <div><b>Part 3:</b> {env.part_notes.part_3}</div>}
                    </div>
                  </div>
                )}
                {env.fluency_patterns?.length > 0 && (
                  <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="ielts-speaking-fluency">
                    <div className="text-sm font-bold text-[#172033]">Fluency patterns</div>
                    <div className="mt-2 space-y-2">
                      {env.fluency_patterns.map((p, i) => (
                        <div key={i} className="text-xs text-slate-700">
                          <b>{p.pattern}</b>
                          {p.example && <div className="italic text-slate-500">"{p.example}"</div>}
                          {p.note && <div className="text-emerald-700">→ {p.note}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {(env.priority_improvements?.length > 0 || env.recommended_next_practice) && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                {env.priority_improvements?.length > 0 && (
                  <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4" data-testid="ielts-speaking-priority">
                    <div className="text-sm font-bold text-indigo-900">Priority improvements</div>
                    <ol className="mt-2 list-decimal list-inside text-xs text-slate-800 space-y-1">
                      {env.priority_improvements.map((p, i) => <li key={i}>{p}</li>)}
                    </ol>
                  </div>
                )}
                {env.recommended_next_practice && (
                  <div className="rounded-xl bg-orange-50 border border-orange-100 p-4" data-testid="ielts-speaking-recommendation">
                    <div className="text-sm font-bold text-orange-900 flex items-center gap-1"><Compass className="h-4 w-4" /> Recommended next practice</div>
                    <div className="mt-2 text-sm font-bold text-[#172033]">{critLabel(env.recommended_next_practice.focus)}</div>
                    <div className="text-xs text-slate-700 mt-1">{env.recommended_next_practice.reason}</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
